import { styled } from '@mui/system';
import { TableCell, TableRow, Box, Typography, TextField } from '@mui/material';

// General row layout for grid alignment
export const StyledTableRow = styled(TableRow)(() => ({
  display: 'grid',
  gridTemplateColumns: `200px repeat(7, minmax(104px, 1fr))`,
  alignItems: 'center',
  padding: '0 8px',
  width: '100%', // Ensures it respects its container
  boxSizing: 'border-box',
}));

// Styled table cell for the title section
export const StyledTableTitle = styled(TableCell)(() => ({
  gridColumn: '1 / 2',
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  textAlign: 'end',
  padding: '0 8px',
  color: '#EBEBF599',
  minWidth: 0, // Prevents overflow issues
}));

// **Shift Cell**
export const StyledShiftBox = styled(TableCell)(({ theme }) => ({
  width: '100%',
  minWidth: theme.spacing(13),
  padding: '.5rem',
  justifyItems: 'start',
  display: 'flex',
  boxSizing: 'border-box',
  overflow: 'hidden',
  transition: 'background 0.3s ease, border-color 0.3s ease',
  background: '#1C1C1E',
  fontStyle: 'normal',
  fontWeight: 400,
  fontSize: '17px',
  lineHeight: '22px',
  letterSpacing: '-0.4px',
  height: '4rem',
  alignItems: 'center', // Center content vertically

  // Border radius variations
  '&.single': {
    borderRadius: '10px',
  },
  '&.first': {
    borderRadius: '10px 0 0 10px',
  },
  '&.last': {
    borderRadius: '0 10px 10px 0',
  },

  // State variations
  '&.current-day': {
    color: '#FFFFFF',
    background: '#2C2C2E',
    '&:hover': {
      background: '#3C3C3E',
    },
  },
  '&.selected': {
    color: '#FFFFFF',
    background: '#3A3A3C',
    '&:hover': {
      background: '#2C2C2E',
    },
  },

  // Default hover state
  '&:hover': {
    background: '#2C2C2E',
    cursor: 'pointer',
  },

  '&:focus': {
    outline: 'none',
    boxShadow: '0 0 0 2px #007AFF',
  },
}));

// Flex container base styles
const FlexBox = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
}));

// Content container with space-between
export const NoteContentBox = styled(FlexBox)(({ theme }) => ({
  width: '100%',
  justifyContent: 'space-between',
  gap: theme.spacing(0.25), // 2px equivalent
}));

// Container for note content and type
export const NoteContentWrapper = styled(FlexBox)(() => ({
  flex: 1,
  overflow: 'hidden',
}));

// Container for grouped icons
export const IconsContainer = styled(FlexBox)(({ theme }) => ({
  flexDirection: 'column',
  gap: theme.spacing(1),
  flexShrink: 0,
  marginLeft: 'auto',
  height: '100%',
  justifyContent: 'center',
}));

// Base styles for icon containers
const IconContainerBase = styled(FlexBox)(() => ({
  cursor: 'pointer',
  width: '24px',
  height: '24px',
  justifyContent: 'center',
  transition: 'background-color 0.2s ease',
  '& .MuiSvgIcon-root': {
    fontSize: '16px',
    color: 'white',
  }
}));

// Specific icon containers
export const OpenIcon = styled(IconContainerBase)(({ theme }) => ({
  backgroundColor: theme.palette.secondary.main,
  borderRadius: '6px',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
  }
}));

export const NotesTypography = styled(Typography)(() => ({
  flex: 1,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

export const NoteTypeIndicator = styled(FlexBox)(({ type, theme }) => ({
  justifyContent: 'center',
  width: '24px',
  height: '24px',
  borderRadius: '6px',
  fontWeight: 'bold',
  backgroundColor: theme.palette.background.secondary,
  border: '1px solid rgba(255, 255, 255, 0.1)',
  color: type === 'M' ? '#1a73e8' : '#34a853',
  flexShrink: 0,
}));

export const ActionButtons = styled(FlexBox)(({ theme }) => ({
  justifyContent: 'space-between',
  marginTop: 'auto',
  gap: theme.spacing(2)
}));

export const BaseModal = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  backgroundColor: theme.palette.background.secondary,
  padding: '24px',
  borderRadius: '8px',
  outline: 'none',
  width: '400px',
}));

export const NotesModal = styled(BaseModal)(({ theme }) => ({
  minHeight: '200px',
  display: 'flex',
  flexDirection: 'column',

  '& .MuiTextField-root': {
    flex: 1,
    marginBottom: '8px',
    minHeight: '80px',
  },
  '& .MuiButton-root': {
    minWidth: '100px',
  }
}));

export const StyledTextField = styled(TextField)(() => ({
  marginBottom: 2,
  width: '100%',
  '& .MuiInputBase-input': {
    color: 'white',
    overflow: 'auto',
  },
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.23)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.5)',
    },
  }
}));

//NotesDetailView.JS
export const DetailViewContainer = styled(BaseModal)(({ theme }) => ({
  color: theme.palette.text.primary,
}));

export const NoteSection = styled(Box)(({ theme }) => ({
  marginBottom: '16px',
  padding: '12px',
  backgroundColor: theme.palette.background.selected,
  borderRadius: '8px',
}));
